import { utcToLocal } from '@/utils/date/dateUtils';
import { cn } from '@/utils/styles';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
} from '@design-system/Sheet';
import { useGetKitStatus } from '@viome/viome-base/src/usecase/Home/HomeQuery';
import { X } from 'lucide-react';
import { Timeline } from './Timeline';
import { TrackingSampleSkeleton } from './TrackingSampleSkeleton';

interface TrackingSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TrackingSampleModal = ({
  isOpen,
  onClose,
}: TrackingSampleModalProps) => {
  const { data: testStatus, isLoading } = useGetKitStatus();

  if (!isOpen) return null;

  if (isLoading) {
    return <TrackingSampleSkeleton isOpen={isOpen} onClose={onClose} />;
  }

  if (!testStatus) return null;

  const localTime = utcToLocal(testStatus?.updated_at);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetPortal>
        <SheetOverlay />
        <SheetContent
          side="right"
          className={cn(
            'pr-3 pl-6',
            'bg-background-secondary',
            'shadow-[0px_1px_0px_0px_#FFFFFF1A_inset,0px_4px_40px_0px_#44445E29]',
          )}
          variant="glass"
          hideCloseButton
        >
          <SheetHeader className="mb-0 pt-6 pl-1 mt-3">
            <div>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h2 className="typography-h2 text-primary font-semibold">
                    Track My Sample
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-static-secondary hover:text-static-primary transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="text-caption1 text-sm text-secondary mt-8">
                Last updated on {localTime}
              </p>
            </div>
          </SheetHeader>

          <div className="relative overflow-y-auto px-1 pb-8">
            <Timeline
              steps={testStatus.detailed_track_status_list}
              currentStepId={testStatus.detailed_track_status_id}
              hasIssue={testStatus.is_issue_in_current_status}
              errorText={testStatus.status_header}
            />
          </div>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
};
